import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AppointmentSkeletonComponent } from '../shared/components/appointment-skeleton/appointment-skeleton.component';
import { AuthService } from '../_services/auth.service';
import { User } from '../_interfaces/user';
import { ApiService } from '../_services/api.service';
import { Subject } from '../_interfaces/subject';
import { DialogService } from '../shared/components/dialog/dialog.service';
import { StartEncounterComponent } from '../_dialogs/start-encounter/start-encounter.component';
import { ManualPatientSelectorComponent } from '../_dialogs/manual-patient-selector/manual-patient-selector.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatSubjectName, isToday, findMostRelevantEncounterIndex, scrollScheduleToIndex } from '../_helpers/utils';
import { formatDmyDate, calendarDayOffset } from '../_helpers/dmy-date';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EncounterService } from '../_services/encounter.service';
import { LoadingVisibilityController } from '../shared/utils/loading-visibility';
import { FeatureFlagsService } from '../_services/feature-flags.service';
import { AnalyticsService } from '../_services/analytics.service';

@Component({
  selector: 'app-home',
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    RouterLink,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    AppointmentSkeletonComponent,
    TranslateModule,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  host: {
    class: 'block h-full p-4 md:px-10 md:pt-8 md:pb-14'
  }
})
export class HomeComponent implements OnDestroy {
  displayDate: Date = new Date();
  searchQuery = '';
  private user: User;
  subjects: Subject[] = [];
  isLoading = false;
  showLoadingSkeleton = false;
  isLegalOrg = false;
  formatSubjectName = formatSubjectName;
  private readonly loadingVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingSkeleton = visible;
  });

  @ViewChild('scheduleList') scheduleList!: ElementRef<HTMLDivElement>;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private dialog: DialogService,
    private snackBar: MatSnackBar,
    private encounterService: EncounterService,
    private translate: TranslateService,
    private flags: FeatureFlagsService,
    private analytics: AnalyticsService,
  ) {
    this.user = this.authService.getUser() as User;
    this.isLegalOrg = this.user?.entity?.settings?.category === 'Legal';
  }

  get isUploadAudioEnabled(): boolean {
    return this.flags.isEnabled('uploadAudio');
  }

  get isSmartDictationEnabled(): boolean {
    return this.flags.isEnabled('smartDictation');
  }

  async ngOnInit(): Promise<void> {
    await this.loadSubjects();
  }

  ngOnDestroy(): void {
    this.loadingVisibility.destroy();
  }

  get userName(): string {
    return this.user.profile?.first_name ?? 'User';
  }

  get isDictateEnabled(): boolean {
    return this.user.entity.settings.enableDictate === true;
  }

  get greeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return this.translate.instant('Good morning');
    if (hour < 18) return this.translate.instant('Good afternoon');
    return this.translate.instant('Good evening');
  }

  get isToday(): boolean {
    const today = new Date();
    return (
      this.displayDate.getFullYear() === today.getFullYear() &&
      this.displayDate.getMonth() === today.getMonth() &&
      this.displayDate.getDate() === today.getDate()
    );
  }

  get dateLabel(): string {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfDisplay = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), this.displayDate.getDate());
    const diffDays = Math.round((startOfDisplay.getTime() - startOfToday.getTime()) / 86400000);
    if (diffDays === -1) return this.translate.instant('Yesterday');
    if (diffDays === 0) return this.translate.instant('Today');
    if (diffDays === 1) return this.translate.instant('Tomorrow');
    
    // For other days, show the translated weekday name
    const weekdayIndex = this.displayDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return this.translate.instant(weekdays[weekdayIndex]);
  }

  get formattedDate(): string {
    return formatDmyDate(this.displayDate);
  }

  prevDay(): void {
    this.displayDate = new Date(this.displayDate.getTime() - 86400000);
    this.trackScheduledView('day_navigation');
    this.loadSubjects();
  }

  nextDay(): void {
    this.displayDate = new Date(this.displayDate.getTime() + 86400000);
    this.trackScheduledView('day_navigation');
    this.loadSubjects();
  }

  setToday(): void {
    this.displayDate = new Date();
    this.trackScheduledView('back_to_today');
    this.loadSubjects();
  }

  get filteredItems() {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.subjects;
    return this.subjects.filter(item =>
      (`${item.subject_first_name} ${item.subject_middle_name} ${item.subject_last_name}`.trim().toLowerCase().includes(q)) ||
      (item.subject_id?.toLowerCase().includes(q)) ||
      (item.subject_alternative_id?.toLowerCase?.().includes(q) ?? false) ||
      (formatDmyDate(new Date(item.subject_dob)).includes(q))
    );
  }

  onSearchCleared(): void {
    if (!this.searchQuery.trim()) {
      this.scrollToTop();
    }
  }

  private trackScheduledView(
    trigger: 'day_navigation' | 'back_to_today',
  ): void {
    if (!this.isDictateEnabled) {
      return;
    }
    this.analytics.track('schedule_viewed', {
      day_offset: calendarDayOffset(this.displayDate),
      trigger,
    });
  }

  private scrollToTop(): void {
    const container = this.scheduleList?.nativeElement;
    if (container) {
      container.scrollTop = 0;
    }
  }

  private scrollToRelevantEncounter(): void {
    if (!isToday(this.displayDate) || this.subjects.length === 0) return;

    const index = findMostRelevantEncounterIndex(this.subjects);
    scrollScheduleToIndex(this.scheduleList?.nativeElement, index);
  }

  private async loadSubjects(): Promise<void> {
    if (!this.isDictateEnabled) {
      this.subjects = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.loadingVisibility.start();
    try {
      this.subjects = await this.apiService.getSubjects({ date: this.displayDate, allSubjects: false });
      this.scrollToRelevantEncounter();
    } catch (e) {
      this.subjects = [];
      this.snackBar.open(this.translate.instant('Unable to load patients.'), this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    } finally {
      this.isLoading = false;
      this.loadingVisibility.stop();
    }
  }

  formatTime(item: Subject): string {
    if (!item.admit_date) return '';
    const d = new Date(item.admit_date);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  formatDob(item: Subject): string {
    return formatDmyDate(new Date(item.subject_dob));
  }

  async startEncounter(mode: 'consultation' | 'dictation'): Promise<void> {
    if (this.isDictateEnabled) {
      this.dialog.open(StartEncounterComponent, {
        size: 'lg',
        maxHeight: '100vh',
        data: { mode: mode }
      });
    } else {
      this.openManualPatientSelector(mode);
    }
  }

  private openManualPatientSelector(mode: 'consultation' | 'dictation'): void {
    this.dialog.open(ManualPatientSelectorComponent, {
      size: 'sm',
      data: { from: 'consultation', mode: mode }
    });
  }

  async startEncounterFor(patient: Subject, listIndex: number): Promise<void> {
    this.analytics.track('visit_selected', {
      // Internal schedule-row id. MRNs and clinical visit numbers stay off the event.
      visit_id: String(patient.id),
      day_offset: calendarDayOffset(this.displayDate),
      list_position: listIndex + 1,
    });
    await this.encounterService.createAndNavigate(patient, 'consultation', { entryPoint: 'schedule_home' });
  }
}
